import React, { useCallback, useEffect, useState } from 'react';
import { WebhookConfig, WebhookDelivery, WebhookEventConfig, webhookService } from '../services/api';

const CHANNEL_LABELS: Record<WebhookEventConfig['channel'], string> = {
  staff: 'Staff channel',
  public: 'Public channel',
  custom: 'Custom webhook',
};

type Draft = Record<string, { enabled: boolean; channel: WebhookEventConfig['channel']; url: string; users: string; roles: string }>;

function toDraft(config: WebhookConfig): Draft {
  return Object.fromEntries(config.events.map((event) => [event.key, {
    enabled: event.enabled,
    channel: event.channel,
    url: '',
    users: event.mention_users.join(', '),
    roles: event.mention_roles.join(', '),
  }]));
}

const sourceNote = (source: 'env' | 'file' | null) => (source === 'env' ? 'Set in server environment' : source === 'file' ? 'Saved in settings file' : 'Not set');

/** Owner-only Discord webhook settings: destinations, per-event options, tests, and delivery log. */
const WebhookSettingsPanel: React.FC = () => {
  const [config, setConfig] = useState<WebhookConfig | null>(null);
  const [draft, setDraft] = useState<Draft>({});
  const [staffUrl, setStaffUrl] = useState('');
  const [publicUrl, setPublicUrl] = useState('');
  const [mentionId, setMentionId] = useState('');
  const [roleId, setRoleId] = useState('');
  const [log, setLog] = useState<WebhookDelivery[]>([]);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const apply = (next: WebhookConfig) => {
    setConfig(next);
    setDraft(toDraft(next));
    setMentionId(next.default_mention_user_id || '');
    setRoleId(next.public_role_id || '');
    setStaffUrl('');
    setPublicUrl('');
  };

  const loadLog = useCallback(() => {
    webhookService.deliveries(25).then((res) => setLog(res.data)).catch(() => undefined);
  }, []);

  useEffect(() => {
    webhookService.get().then((res) => apply(res.data)).catch(() => setError('Unable to load webhook settings.'));
    loadLog();
  }, [loadLog]);

  if (!config) {
    return error ? <div className="alert alert-error">{error}</div> : <div className="card">Loading webhook settings...</div>;
  }

  const save = async () => {
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const payload: Record<string, unknown> = {
        events: Object.fromEntries(Object.entries(draft).map(([key, value]) => [key, {
          enabled: value.enabled,
          channel: value.channel,
          ...(value.url.trim() ? { url: value.url.trim() } : {}),
          mention_users: value.users,
          mention_roles: value.roles,
        }])),
      };
      if (staffUrl.trim() && config.sources.staff_url !== 'env') payload.staff_url = staffUrl.trim();
      if (publicUrl.trim() && config.sources.public_url !== 'env') payload.public_url = publicUrl.trim();
      if (config.sources.mention !== 'env') payload.default_mention_user_id = mentionId.trim();
      if (config.sources.public_role !== 'env') payload.public_role_id = roleId.trim();
      const res = await webhookService.update(payload);
      apply(res.data);
      setMessage('Webhook settings saved.');
    } catch (requestError: any) {
      setError(requestError?.response?.data?.error || 'Failed to save webhook settings.');
    } finally {
      setSaving(false);
    }
  };

  const toggleAll = async (enabled: boolean) => {
    try {
      const res = await webhookService.update({ enabled });
      apply(res.data);
    } catch (requestError: any) {
      setError(requestError?.response?.data?.error || 'Failed to update webhooks.');
    }
  };

  const sendTest = async (event: string) => {
    setTesting(event);
    setError(null);
    setMessage(null);
    try {
      await webhookService.test(event);
      setMessage('Test message sent. Check the Discord channel.');
    } catch (requestError: any) {
      setError(`Test failed: ${requestError?.response?.data?.error || 'delivery error'}`);
    } finally {
      setTesting(null);
      loadLog();
    }
  };

  const update = (key: string, patch: Partial<Draft[string]>) => setDraft((current) => ({ ...current, [key]: { ...current[key], ...patch } }));

  return (
    <div className="module-card">
      <div className="webhook-event-head">
        <h3 className="section-heading">Discord webhooks</h3>
        <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 10, margin: 0 }}>
          <input type="checkbox" checked={config.enabled} onChange={(event) => toggleAll(event.target.checked)} />
          Notifications on
        </label>
      </div>
      <p className="field-help">
        Webhook URLs are secrets. They are shown masked and can be replaced, but never displayed in full.
        {config.public_base_url ? ` Links point to ${config.public_base_url}.` : ' Set PUBLIC_BASE_URL on the server so messages include links.'}
      </p>

      {error && <div className="alert alert-error">{error}</div>}
      {message && <div className="alert alert-success">{message}</div>}

      <div className="form-grid two-column">
        <div className="form-group">
          <label className="form-label" htmlFor="wh-staff">Staff channel webhook</label>
          <input id="wh-staff" className="form-input" value={staffUrl} disabled={config.sources.staff_url === 'env'}
            placeholder={config.staff_url_masked || 'https://discord.com/api/webhooks/…'} onChange={(e) => setStaffUrl(e.target.value)} />
          <p className="field-help">{sourceNote(config.sources.staff_url)}. Review queue and workflow updates.</p>
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="wh-public">Public channel webhook</label>
          <input id="wh-public" className="form-input" value={publicUrl} disabled={config.sources.public_url === 'env'}
            placeholder={config.public_url_masked || 'Optional — falls back to the staff channel'} onChange={(e) => setPublicUrl(e.target.value)} />
          <p className="field-help">{sourceNote(config.sources.public_url)}. Announcements when discussions are published.</p>
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="wh-mention">Default staff mention (user ID)</label>
          <input id="wh-mention" className="form-input" value={mentionId} disabled={config.sources.mention === 'env'}
            onChange={(e) => setMentionId(e.target.value)} placeholder="e.g. 123456789012345678" />
          <p className="field-help">{sourceNote(config.sources.mention)}. Used by events that have no mention list saved yet.</p>
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="wh-role">Public role ID (optional)</label>
          <input id="wh-role" className="form-input" value={roleId} disabled={config.sources.public_role === 'env'}
            onChange={(e) => setRoleId(e.target.value)} placeholder="Not pinged unless added to an event" />
          <p className="field-help">{sourceNote(config.sources.public_role)}. Add it to an event's role mentions to ping it.</p>
        </div>
      </div>

      <h4 className="section-heading" style={{ marginTop: 16 }}>Events</h4>
      {config.events.map((event) => {
        const value = draft[event.key];
        if (!value) return null;
        return (
          <div key={event.key} className="webhook-event-row">
            <div className="webhook-event-head">
              <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 10, margin: 0 }}>
                <input type="checkbox" checked={value.enabled} onChange={(e) => update(event.key, { enabled: e.target.checked })} />
                <strong>{event.label}</strong>
              </label>
              <button type="button" className="btn btn-secondary" disabled={!event.has_destination || testing === event.key} onClick={() => sendTest(event.key)}>
                {testing === event.key ? 'Sending...' : 'Send test'}
              </button>
            </div>
            <p className="field-help" style={{ margin: 0 }}>{event.description}{!event.has_destination ? ' No destination configured.' : ''}</p>
            <div className="form-grid two-column">
              <div className="form-group">
                <label className="form-label" htmlFor={`wh-${event.key}-channel`}>Destination</label>
                <select id={`wh-${event.key}-channel`} className="form-input" value={value.channel}
                  onChange={(e) => update(event.key, { channel: e.target.value as WebhookEventConfig['channel'] })}>
                  {Object.entries(CHANNEL_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                </select>
              </div>
              {value.channel === 'custom' && (
                <div className="form-group">
                  <label className="form-label" htmlFor={`wh-${event.key}-url`}>Custom webhook URL</label>
                  <input id={`wh-${event.key}-url`} className="form-input" value={value.url}
                    placeholder={event.custom_url_masked || 'https://discord.com/api/webhooks/…'} onChange={(e) => update(event.key, { url: e.target.value })} />
                </div>
              )}
              <div className="form-group">
                <label className="form-label" htmlFor={`wh-${event.key}-users`}>Mention users (IDs)</label>
                <input id={`wh-${event.key}-users`} className="form-input" value={value.users} placeholder="None"
                  onChange={(e) => update(event.key, { users: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor={`wh-${event.key}-roles`}>Mention roles (IDs)</label>
                <input id={`wh-${event.key}-roles`} className="form-input" value={value.roles} placeholder="None"
                  onChange={(e) => update(event.key, { roles: e.target.value })} />
              </div>
            </div>
          </div>
        );
      })}

      <div className="action-row" style={{ marginTop: 10 }}>
        <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save webhook settings'}</button>
      </div>

      <div className="webhook-event-head" style={{ marginTop: 20 }}>
        <h4 className="section-heading" style={{ margin: 0 }}>Recent deliveries</h4>
        <button type="button" className="btn btn-secondary" onClick={loadLog}>Refresh</button>
      </div>
      {log.length === 0 ? (
        <p className="field-help">No deliveries yet.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="webhook-log-table">
            <thead>
              <tr><th>Time</th><th>Event</th><th>Discussion</th><th>Channel</th><th>Result</th></tr>
            </thead>
            <tbody>
              {log.map((entry, index) => (
                <tr key={`${entry.at}-${index}`}>
                  <td>{new Date(entry.at).toLocaleString()}</td>
                  <td>{entry.event === 'test' ? `Test (${entry.test_event})` : entry.event}</td>
                  <td>{entry.discussion_title || '—'}</td>
                  <td>{entry.channel}</td>
                  <td className={entry.ok ? 'text-success' : 'text-danger'}>
                    {entry.ok ? `Delivered${entry.attempts > 1 ? ` after ${entry.attempts} tries` : ''}` : `Failed: ${entry.error || entry.status}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default WebhookSettingsPanel;
